// Inspector pane — renders a form for the current primary selection. Each
// edit clones the json down to the mutation point and commits with the
// appropriate kind ('structural' for adds/removes/material swaps, 'transform'
// for transform-only edits, 'visual' for color/material tweaks, 'meta' for
// label/desc).
//
// Material editor: structured fields (color picker + sliders) instead of a
// raw JSON textarea, plus preset combobox and module reference.

import type { EditorState, CommitKind } from './EditorState';
import type {
  BehaviorDef, LightDef, MapJSON, MaterialRef, ObjectNode, Vec3
} from '../../maps/MapJSON';
import { isMaterialPreset, isMaterialModule } from '../../maps/MapJSON';
import { renderIcon, type IconNode } from './icons';
import { registerAssetFromFile, makeAssetUrl, isAssetUrl, parseAssetUrl, getAsset } from './EditorAssets';
import {
  Settings, Cloud, Lightbulb, Box, Cog, Music, Image as ImageIcon, Move,
  Maximize2, Rotate3d, Palette, Trash2, Upload, ChevronDown, Pyramid, Cuboid
} from 'lucide';

interface SectionState { collapsed: boolean }

export class InspectorPane {
  readonly root: HTMLElement;
  private state: EditorState;
  private unsubDoc: () => void;
  private unsubSel: () => void;
  private sections = new Map<string, SectionState>();

  constructor(parent: HTMLElement, state: EditorState) {
    this.state = state;
    this.root = document.createElement('div');
    this.root.className = 'ed-pane-inspector-inner';
    parent.appendChild(this.root);
    this.unsubDoc = this.state.subscribeDoc(() => this.render());
    this.unsubSel = this.state.subscribeSelection(() => this.render());
    this.render();
  }

  dispose(): void {
    this.unsubDoc();
    this.unsubSel();
    this.root.remove();
  }

  private render(): void {
    const { json, primarySelectionId, selectionIds } = this.state.get();
    this.root.innerHTML = '';
    if (!primarySelectionId) {
      this.root.innerHTML = '<div class="ed-empty">Select a node on the left to edit, or click in the viewport.</div>';
      return;
    }
    if (selectionIds.length > 1) {
      this.renderMultiSelect(selectionIds);
      // Continue rendering primary selection below, so user can still edit one.
    }
    if (primarySelectionId === 'map') return this.renderMapProps(json);
    if (primarySelectionId === 'sky') return this.renderSky(json);
    if (primarySelectionId === 'ibl') return this.renderIbl(json);
    if (primarySelectionId === 'picker') return this.renderPicker(json);
    if (primarySelectionId === 'audio') return this.renderAudio(json);
    if (primarySelectionId.startsWith('light:')) return this.renderLight(json, primarySelectionId.slice(6));
    if (primarySelectionId.startsWith('obj:')) return this.renderObject(json, primarySelectionId.slice(4));
    if (primarySelectionId.startsWith('behavior:')) return this.renderBehavior(json, primarySelectionId.slice(9));
    this.root.innerHTML = '<div class="ed-empty">(no inspector for this node)</div>';
  }

  // ---------------------------------------------------------------------
  // Multi-select banner (only shows count + an unselect-all button)

  private renderMultiSelect(ids: string[]): void {
    const banner = document.createElement('div');
    banner.className = 'ed-h2';
    banner.innerHTML = `${renderIcon(Settings)} <span>${ids.length} selected</span>`;
    const sub = document.createElement('div');
    sub.className = 'ed-subtle';
    sub.textContent = 'Editing properties of the most-recent selection. Use the gizmo to move all together.';
    this.root.appendChild(banner);
    this.root.appendChild(sub);
  }

  // ---------------------------------------------------------------------
  // Map root

  private renderMapProps(json: MapJSON): void {
    this.h2('Map Properties', Settings);
    const body = this.section('main', 'General', Settings);
    this.textField(body, 'Label', json.label, (v) => this.commit({ ...json, label: v }, 'meta'));
    this.textField(body, 'Description', json.description, (v) => this.commit({ ...json, description: v }, 'meta'));
    this.textField(body, 'Loading hint', json.loadingHint ?? '', (v) =>
      this.commit({ ...json, loadingHint: v || undefined }, 'meta'));

    const physics = this.section('physics', 'Physics & World', Cog);
    const props = json.properties ?? {};
    this.numberField(physics, 'Gravity Y', props.gravity ?? -22, (v) =>
      this.commit({ ...json, properties: { ...props, gravity: v } }, 'meta'));
    this.numberField(physics, 'Kill Y', props.killY ?? -8, (v) =>
      this.commit({ ...json, properties: { ...props, killY: v } }, 'meta'));
    this.numberField(physics, 'Radius', props.radius ?? 7, (v) =>
      this.commit({ ...json, properties: { ...props, radius: v } }, 'meta'));
    this.numberField(physics, 'Sim speed', props.simSpeed ?? 1, (v) =>
      this.commit({ ...json, properties: { ...props, simSpeed: v } }, 'meta'));

    const sun = this.section('sun', 'Sun Direction', Lightbulb);
    this.vec3Field(sun, 'Direction', props.sunDir ?? [0, 1, 0], (v) =>
      this.commit({ ...json, properties: { ...props, sunDir: v } }, 'structural'));
  }

  // ---------------------------------------------------------------------
  // Sky

  private renderSky(json: MapJSON): void {
    this.h2('Sky', Cloud);
    const sky = json.sky ?? { preset: '' };
    const body = this.section('sky', 'Preset & Params', Cloud);
    this.selectField(body, 'Preset', sky.preset, [
      'sandboxSky', 'space', 'moonSky', 'saturn', 'airshipSky'
    ], (v) => this.commit({ ...json, sky: { ...sky, preset: v } }, 'structural'));
    this.jsonField(body, 'Params (JSON)', sky.params ?? {}, (next) =>
      this.commit({ ...json, sky: { ...sky, params: next } }, 'structural'));
  }

  // ---------------------------------------------------------------------
  // IBL

  private renderIbl(json: MapJSON): void {
    this.h2('Image-Based Lighting', ImageIcon);
    const ibl = json.ibl ?? {};
    const main = this.section('ibl-main', 'IBL', ImageIcon);
    this.selectField(main, 'Mode', ibl.mode ?? 'roomDefault', [
      'roomDefault', 'roomAligned', 'hemisphere', 'hdr'
    ], (v) => this.commit({ ...json, ibl: { ...ibl, mode: v as NonNullable<MapJSON['ibl']>['mode'] } }, 'structural'));

    this.sliderField(main, 'Intensity', ibl.intensity ?? 1, 0, 4, 0.01, (v) =>
      this.commit({ ...json, ibl: { ...ibl, intensity: v } }, 'visual'));
    this.sliderField(main, 'Rotation°', ibl.rotationDeg ?? 0, -180, 180, 1, (v) =>
      this.commit({ ...json, ibl: { ...ibl, rotationDeg: v } }, 'visual'));

    if (ibl.mode === 'hemisphere') {
      const hemi = this.section('ibl-hemi', 'Hemisphere', Cloud);
      this.colorField(hemi, 'Sky', ibl.hemiSky ?? '#88aaff', (v) =>
        this.commit({ ...json, ibl: { ...ibl, hemiSky: v } }, 'visual'));
      this.colorField(hemi, 'Ground', ibl.hemiGround ?? '#222222', (v) =>
        this.commit({ ...json, ibl: { ...ibl, hemiGround: v } }, 'visual'));
      this.sliderField(hemi, 'Hemi int', ibl.hemiIntensity ?? 1, 0, 4, 0.01, (v) =>
        this.commit({ ...json, ibl: { ...ibl, hemiIntensity: v } }, 'visual'));
    }

    if (ibl.mode === 'hdr') {
      const hdr = this.section('ibl-hdr', 'HDR Environment', ImageIcon);
      this.uploadField(hdr, 'HDR file', ibl.hdrPath ?? '', '.hdr,application/octet-stream', async (file) => {
        const asset = await registerAssetFromFile(file);
        this.commit({ ...json, ibl: { ...ibl, hdrPath: makeAssetUrl(asset.id) } }, 'structural');
      });
      const cur = ibl.hdrPath ?? '';
      const label = isAssetUrl(cur) ? `(asset: ${getAsset(parseAssetUrl(cur)!)?.name ?? '?'})` : cur || '(none)';
      const lbl = document.createElement('div');
      lbl.className = 'ed-subtle';
      lbl.textContent = label;
      hdr.appendChild(lbl);
    }
  }

  // ---------------------------------------------------------------------
  // Picker card

  private renderPicker(json: MapJSON): void {
    this.h2('Picker Card', ImageIcon);
    const card = json.pickerCard ?? { thumbnail: '', rotationDeg: 0, left: 0, top: 0 };
    const body = this.section('picker', 'Card Layout', ImageIcon);
    this.uploadField(body, 'Thumbnail', card.thumbnail, '.png,.jpg,.jpeg,image/png,image/jpeg', async (file) => {
      const asset = await registerAssetFromFile(file);
      this.commit({ ...json, pickerCard: { ...card, thumbnail: makeAssetUrl(asset.id) } }, 'meta');
    });
    this.textField(body, 'Thumbnail URL', card.thumbnail, (v) =>
      this.commit({ ...json, pickerCard: { ...card, thumbnail: v } }, 'meta'));
    this.numberField(body, 'Rotation°', card.rotationDeg, (v) =>
      this.commit({ ...json, pickerCard: { ...card, rotationDeg: v } }, 'meta'));
    this.numberField(body, 'Left', card.left, (v) =>
      this.commit({ ...json, pickerCard: { ...card, left: v } }, 'meta'));
    this.numberField(body, 'Top', card.top, (v) =>
      this.commit({ ...json, pickerCard: { ...card, top: v } }, 'meta'));
  }

  // ---------------------------------------------------------------------
  // Audio

  private renderAudio(json: MapJSON): void {
    this.h2('Audio', Music);
    const audio = json.audio ?? {};
    const main = this.section('audio-main', 'Music', Music);
    this.textField(main, 'Music URL', audio.music ?? '', (v) =>
      this.commit({ ...json, audio: { ...audio, music: v || undefined } }, 'structural'));
    this.sliderField(main, 'Music vol', audio.musicVolume ?? 0.7, 0, 2, 0.01, (v) =>
      this.commit({ ...json, audio: { ...audio, musicVolume: v } }, 'meta'));

    const reverbBody = this.section('audio-reverb', 'Reverb', Music);
    const reverb = audio.reverb;
    this.checkboxField(reverbBody, 'Enabled', !!reverb, (on) => {
      const next = on ? { durationSec: 4, decay: 2.5, amplitude: 0.55 } : undefined;
      this.commit({ ...json, audio: { ...audio, reverb: next } }, 'structural');
    });
    if (reverb) {
      this.textField(reverbBody, 'IR URL', reverb.irUrl ?? '', (v) =>
        this.commit({ ...json, audio: { ...audio, reverb: { ...reverb, irUrl: v || undefined } } }, 'structural'));
      this.sliderField(reverbBody, 'wet', reverb.wet ?? 1, 0, 4, 0.01, (v) =>
        this.commit({ ...json, audio: { ...audio, reverb: { ...reverb, wet: v } } }, 'meta'));
      this.numberField(reverbBody, 'durationSec', reverb.durationSec ?? 4, (v) =>
        this.commit({ ...json, audio: { ...audio, reverb: { ...reverb, durationSec: v } } }, 'structural'));
      this.numberField(reverbBody, 'decay', reverb.decay ?? 2.5, (v) =>
        this.commit({ ...json, audio: { ...audio, reverb: { ...reverb, decay: v } } }, 'structural'));
      this.numberField(reverbBody, 'amplitude', reverb.amplitude ?? 0.55, (v) =>
        this.commit({ ...json, audio: { ...audio, reverb: { ...reverb, amplitude: v } } }, 'structural'));
    }

    const wetBody = this.section('audio-wet', 'Wet sends', Music);
    this.numberField(wetBody, 'hitReverbWet', audio.hitReverbWet ?? 0, (v) =>
      this.commit({ ...json, audio: { ...audio, hitReverbWet: v } }, 'meta'));
    this.numberField(wetBody, 'swingReverbWet', audio.swingReverbWet ?? 0, (v) =>
      this.commit({ ...json, audio: { ...audio, swingReverbWet: v } }, 'meta'));
  }

  // ---------------------------------------------------------------------
  // Light

  private renderLight(json: MapJSON, id: string): void {
    const lights = json.lights ?? [];
    const idx = lights.findIndex((l) => (l.id ?? `(${l.type})`) === id);
    if (idx < 0) {
      this.root.innerHTML = '<div class="ed-empty">(light not found)</div>';
      return;
    }
    const light = lights[idx]!;
    this.h2(`Light · ${light.type}`, Lightbulb);
    const body = this.section('light', 'Properties', Lightbulb);
    this.textField(body, 'Id', light.id ?? '', (v) => this.replaceLight(json, idx, { ...light, id: v || undefined }));
    this.colorField(body, 'Color', (light as { color?: string }).color ?? '#ffffff', (v) =>
      this.replaceLight(json, idx, { ...light, color: v } as LightDef));
    this.sliderField(body, 'Intensity', (light as { intensity?: number }).intensity ?? 1, 0, 8, 0.01, (v) =>
      this.replaceLight(json, idx, { ...light, intensity: v } as LightDef));
    if (light.type === 'point') {
      this.numberField(body, 'Range', light.range ?? 0, (v) =>
        this.replaceLight(json, idx, { ...light, range: v }));
      this.numberField(body, 'Decay', light.decay ?? 2, (v) =>
        this.replaceLight(json, idx, { ...light, decay: v }));
      this.vec3Field(body, 'Position', light.position, (v) =>
        this.replaceLight(json, idx, { ...light, position: v }));
    } else if (light.type === 'directional') {
      this.vec3Field(body, 'Direction', light.direction ?? [0, 1, 0], (v) =>
        this.replaceLight(json, idx, { ...light, direction: v }));
    }

    const actions = document.createElement('div');
    actions.className = 'ed-actions';
    const del = document.createElement('button');
    del.className = 'ed-iconbtn danger';
    del.innerHTML = `${renderIcon(Trash2)} <span class="ed-iconbtn-label">Delete</span>`;
    del.onclick = () => {
      const next = lights.slice();
      next.splice(idx, 1);
      this.commit({ ...json, lights: next }, 'structural');
      this.state.clearSelection();
    };
    actions.appendChild(del);
    this.root.appendChild(actions);
  }

  private replaceLight(json: MapJSON, idx: number, next: LightDef): void {
    const lights = (json.lights ?? []).slice();
    lights[idx] = next;
    this.commit({ ...json, lights }, 'structural');
  }

  // ---------------------------------------------------------------------
  // Object (primitive)

  private renderObject(json: MapJSON, id: string): void {
    const path = findObjectPath(json.objects ?? [], id);
    if (!path) {
      this.root.innerHTML = '<div class="ed-empty">(object not found)</div>';
      return;
    }
    const node = path[path.length - 1]!.node;
    this.h2(`${node.type} · ${node.id ?? ''}`, iconForType(node.type));

    const xform = this.section('xform', 'Transform', Move);
    this.textField(xform, 'Id', node.id ?? '', (v) =>
      this.replaceObject(json, path, { ...node, id: v || undefined }, 'structural'));
    const t = node.transform ?? {};
    this.vec3Field(xform, 'Position', t.p ?? [0, 0, 0], (v) =>
      this.replaceObject(json, path, { ...node, transform: { ...t, p: v } }, 'transform'));
    this.vec3Field(xform, 'Rotation', t.r ?? [0, 0, 0], (v) =>
      this.replaceObject(json, path, { ...node, transform: { ...t, r: v } }, 'transform'));
    const s = typeof t.s === 'number' ? [t.s, t.s, t.s] as Vec3 : (t.s ?? [1, 1, 1]);
    this.vec3Field(xform, 'Scale', s, (v) =>
      this.replaceObject(json, path, { ...node, transform: { ...t, s: v } }, 'transform'));

    const geom = this.section('geom', 'Geometry', Pyramid);
    this.renderGeometryFields(geom, json, path, node);

    if (node.type === 'modelRef') {
      const model = this.section('model', 'Model', Box);
      this.textField(model, 'Source', node.src, (v) =>
        this.replaceObject(json, path, { ...node, src: v }, 'structural'));
      this.numberField(model, 'scaleToMaxDim', node.scaleToMaxDim ?? 0, (v) =>
        this.replaceObject(json, path, { ...node, scaleToMaxDim: v }, 'structural'));
      this.numberField(model, 'yawDeg', node.yawDeg ?? 0, (v) =>
        this.replaceObject(json, path, { ...node, yawDeg: v }, 'structural'));
    }

    const matBody = this.section('material', 'Material', Palette);
    this.renderMaterialFields(matBody, json, path, node);

    const colBody = this.section('collider', 'Collider', Cog);
    this.renderColliderFields(colBody, json, path, node);

    const actions = document.createElement('div');
    actions.className = 'ed-actions';
    const del = document.createElement('button');
    del.className = 'ed-iconbtn danger';
    del.innerHTML = `${renderIcon(Trash2)} <span class="ed-iconbtn-label">Delete</span>`;
    del.onclick = () => this.deleteObject(json, path);
    actions.appendChild(del);
    this.root.appendChild(actions);
  }

  private renderGeometryFields(parent: HTMLElement, json: MapJSON, path: ObjectPath, node: ObjectNode): void {
    if (node.type === 'box') {
      this.vec3Field(parent, 'Size', node.size, (v) =>
        this.replaceObject(json, path, { ...node, size: v }, 'structural'));
    } else if (node.type === 'sphere') {
      this.numberField(parent, 'Radius', node.radius, (v) =>
        this.replaceObject(json, path, { ...node, radius: v }, 'structural'));
    } else if (node.type === 'cylinder') {
      this.numberField(parent, 'radiusTop', node.radiusTop, (v) =>
        this.replaceObject(json, path, { ...node, radiusTop: v }, 'structural'));
      this.numberField(parent, 'radiusBottom', node.radiusBottom, (v) =>
        this.replaceObject(json, path, { ...node, radiusBottom: v }, 'structural'));
      this.numberField(parent, 'Height', node.height, (v) =>
        this.replaceObject(json, path, { ...node, height: v }, 'structural'));
    } else if (node.type === 'capsule') {
      this.numberField(parent, 'Radius', node.radius, (v) =>
        this.replaceObject(json, path, { ...node, radius: v }, 'structural'));
      this.numberField(parent, 'halfHeight', node.halfHeight, (v) =>
        this.replaceObject(json, path, { ...node, halfHeight: v }, 'structural'));
    } else if (node.type === 'plane') {
      const sz = node.size as readonly [number, number];
      const planeRow = document.createElement('div');
      planeRow.className = 'ed-row';
      planeRow.innerHTML = '<label>Size</label>';
      const grid = document.createElement('div');
      grid.className = 'ed-row-2col';
      const a = makeNumeric(sz[0], (v) =>
        this.replaceObject(json, path, { ...node, size: [v, sz[1]] }, 'structural'));
      const b = makeNumeric(sz[1], (v) =>
        this.replaceObject(json, path, { ...node, size: [sz[0], v] }, 'structural'));
      grid.appendChild(a); grid.appendChild(b);
      planeRow.appendChild(grid);
      parent.appendChild(planeRow);
    } else if (node.type === 'circle') {
      this.numberField(parent, 'Radius', node.radius, (v) =>
        this.replaceObject(json, path, { ...node, radius: v }, 'structural'));
    } else {
      const empty = document.createElement('div');
      empty.className = 'ed-subtle';
      empty.textContent = '(no shape parameters)';
      parent.appendChild(empty);
    }
  }

  private renderMaterialFields(parent: HTMLElement, json: MapJSON, path: ObjectPath, node: ObjectNode): void {
    const mat = (node.material ?? {}) as MaterialRef;

    const tabRow = document.createElement('div');
    tabRow.className = 'ed-row-3col';
    const isInline = !isMaterialPreset(mat) && !isMaterialModule(mat);
    const isPreset = isMaterialPreset(mat);
    const isModule = isMaterialModule(mat);
    const mkTab = (label: string, active: boolean, on: () => void) => {
      const b = document.createElement('button');
      b.className = 'ed-iconbtn' + (active ? ' is-active' : '');
      b.innerHTML = `<span class="ed-iconbtn-label">${label}</span>`;
      b.onclick = on;
      return b;
    };
    tabRow.appendChild(mkTab('Inline PBR', isInline, () =>
      this.replaceObject(json, path, { ...node, material: { color: '#cccccc' } }, 'visual')));
    tabRow.appendChild(mkTab('Preset', isPreset, () =>
      this.replaceObject(json, path, { ...node, material: { preset: 'grid' } }, 'structural')));
    tabRow.appendChild(mkTab('Module', isModule, () =>
      this.replaceObject(json, path, { ...node, material: { module: '' } }, 'structural')));
    parent.appendChild(tabRow);

    if (isInline) {
      type InlineMat = {
        color?: string; roughness?: number; metalness?: number;
        emissive?: string; emissiveIntensity?: number;
        mapColor?: string; mapNormal?: string; mapRoughness?: string;
        mapMetalness?: string; mapAo?: string; mapEmissive?: string;
        uvScale?: readonly [number, number];
        uvOffset?: readonly [number, number];
        normalScale?: number; aoIntensity?: number;
      };
      const m = mat as InlineMat;
      this.colorField(parent, 'Color', m.color ?? '#cccccc', (v) =>
        this.replaceObject(json, path, { ...node, material: { ...m, color: v } }, 'visual'));
      this.sliderField(parent, 'Roughness', m.roughness ?? 1, 0, 1, 0.01, (v) =>
        this.replaceObject(json, path, { ...node, material: { ...m, roughness: v } }, 'visual'));
      this.sliderField(parent, 'Metalness', m.metalness ?? 0, 0, 1, 0.01, (v) =>
        this.replaceObject(json, path, { ...node, material: { ...m, metalness: v } }, 'visual'));
      this.colorField(parent, 'Emissive', m.emissive ?? '#000000', (v) =>
        this.replaceObject(json, path, { ...node, material: { ...m, emissive: v } }, 'visual'));
      this.sliderField(parent, 'Emissive int', m.emissiveIntensity ?? 0, 0, 8, 0.01, (v) =>
        this.replaceObject(json, path, { ...node, material: { ...m, emissiveIntensity: v } }, 'visual'));

      // -- Texture maps -----
      const setSlot = <K extends keyof InlineMat>(key: K, value: InlineMat[K]): void => {
        const next = { ...m, [key]: value } as InlineMat;
        this.replaceObject(json, path, { ...node, material: next }, 'structural');
      };
      const slotHeader = document.createElement('div');
      slotHeader.style.cssText = 'font-size:11px;color:var(--fg-mute);text-transform:uppercase;letter-spacing:0.06em;margin-top:6px;font-weight:600';
      slotHeader.textContent = 'Texture maps';
      parent.appendChild(slotHeader);

      this.textureSlot(parent, 'Color', m.mapColor, (url) => setSlot('mapColor', url));
      this.textureSlot(parent, 'Normal', m.mapNormal, (url) => setSlot('mapNormal', url));
      this.textureSlot(parent, 'Roughness', m.mapRoughness, (url) => setSlot('mapRoughness', url));
      this.textureSlot(parent, 'Metalness', m.mapMetalness, (url) => setSlot('mapMetalness', url));
      this.textureSlot(parent, 'AO', m.mapAo, (url) => setSlot('mapAo', url));
      this.textureSlot(parent, 'Emissive', m.mapEmissive, (url) => setSlot('mapEmissive', url));

      const uvHeader = document.createElement('div');
      uvHeader.style.cssText = 'font-size:11px;color:var(--fg-mute);text-transform:uppercase;letter-spacing:0.06em;margin-top:6px;font-weight:600';
      uvHeader.textContent = 'UV';
      parent.appendChild(uvHeader);
      this.vec2Field(parent, 'Scale', m.uvScale ?? [1, 1], (v) =>
        this.replaceObject(json, path, { ...node, material: { ...m, uvScale: v } }, 'structural'));
      this.vec2Field(parent, 'Offset', m.uvOffset ?? [0, 0], (v) =>
        this.replaceObject(json, path, { ...node, material: { ...m, uvOffset: v } }, 'structural'));
      this.sliderField(parent, 'Normal scl', m.normalScale ?? 1, 0, 4, 0.01, (v) =>
        this.replaceObject(json, path, { ...node, material: { ...m, normalScale: v } }, 'visual'));
      this.sliderField(parent, 'AO int', m.aoIntensity ?? 1, 0, 4, 0.01, (v) =>
        this.replaceObject(json, path, { ...node, material: { ...m, aoIntensity: v } }, 'visual'));
    } else if (isPreset) {
      const m = mat as { preset: string; params?: Record<string, unknown> };
      this.selectField(parent, 'Preset', m.preset, [
        'sandboxSky', 'space', 'moonSky', 'saturn', 'airshipSky', 'grid', 'lava', 'moonGround', 'post'
      ], (v) => this.replaceObject(json, path, { ...node, material: { ...m, preset: v } }, 'structural'));
      this.jsonField(parent, 'Params (JSON)', m.params ?? {}, (next) =>
        this.replaceObject(json, path, { ...node, material: { ...m, params: next } }, 'structural'));
    } else {
      const m = mat as { module: string; params?: Record<string, unknown> };
      this.textField(parent, 'Module', m.module, (v) =>
        this.replaceObject(json, path, { ...node, material: { ...m, module: v } }, 'structural'));
      this.jsonField(parent, 'Params (JSON)', m.params ?? {}, (next) =>
        this.replaceObject(json, path, { ...node, material: { ...m, params: next } }, 'structural'));
    }
  }

  private renderColliderFields(parent: HTMLElement, json: MapJSON, path: ObjectPath, node: ObjectNode): void {
    const c = node.collider ?? {};
    this.selectField(parent, 'Body', c.body ?? 'static', ['none', 'static', 'dynamic', 'kinematic'], (v) =>
      this.replaceObject(json, path, { ...node, collider: { ...c, body: v as 'static' } }, 'structural'));
    this.selectField(parent, 'Shape', c.kind ?? 'auto', ['auto', 'box', 'sphere', 'cylinder', 'capsule', 'trimeshFromModel'], (v) =>
      this.replaceObject(json, path, { ...node, collider: { ...c, kind: v as 'auto' } }, 'structural'));
    this.numberField(parent, 'Friction', c.friction ?? 0.9, (v) =>
      this.replaceObject(json, path, { ...node, collider: { ...c, friction: v } }, 'structural'));
    this.numberField(parent, 'Restitution', c.restitution ?? 0.05, (v) =>
      this.replaceObject(json, path, { ...node, collider: { ...c, restitution: v } }, 'structural'));
  }

  private replaceObject(
    json: MapJSON, path: ObjectPath, next: ObjectNode, kind: CommitKind
  ): void {
    const rootArr = (json.objects ?? []).slice();
    let arr = rootArr;
    for (let i = 0; i < path.length - 1; i++) {
      const step = path[i]!;
      const parent = arr[step.index]! as ObjectNode;
      if (parent.type !== 'group') break;
      const nextChildren = (parent.children ?? []).slice();
      arr[step.index] = { ...parent, children: nextChildren };
      arr = nextChildren;
    }
    arr[path[path.length - 1]!.index] = next;
    const syncedIds = kind === 'transform' && next.id ? [next.id] : undefined;
    this.commit({ ...json, objects: rootArr }, kind, syncedIds);
  }

  private deleteObject(json: MapJSON, path: ObjectPath): void {
    const rootArr = (json.objects ?? []).slice();
    let arr = rootArr;
    for (let i = 0; i < path.length - 1; i++) {
      const step = path[i]!;
      const parent = arr[step.index]! as ObjectNode;
      if (parent.type !== 'group') break;
      const nextChildren = (parent.children ?? []).slice();
      arr[step.index] = { ...parent, children: nextChildren };
      arr = nextChildren;
    }
    arr.splice(path[path.length - 1]!.index, 1);
    this.commit({ ...json, objects: rootArr }, 'structural');
    this.state.clearSelection();
  }

  // ---------------------------------------------------------------------
  // Behavior

  private renderBehavior(json: MapJSON, id: string): void {
    const behaviors = json.behaviors ?? [];
    const idx = behaviors.findIndex((b) => b.id === id);
    if (idx < 0) {
      this.root.innerHTML = '<div class="ed-empty">(behavior not found)</div>';
      return;
    }
    const b = behaviors[idx]!;
    this.h2(`Behavior · ${b.id}`, Cog);
    const body = this.section('beh', 'Properties', Cog);
    this.textField(body, 'Id', b.id, (v) => this.replaceBehavior(json, idx, { ...b, id: v }));
    this.textField(body, 'Module', b.module, (v) => this.replaceBehavior(json, idx, { ...b, module: v }));
    this.jsonField(body, 'Params (JSON)', b.params ?? {}, (next) =>
      this.replaceBehavior(json, idx, { ...b, params: next as BehaviorDef['params'] }));

    const actions = document.createElement('div');
    actions.className = 'ed-actions';
    const del = document.createElement('button');
    del.className = 'ed-iconbtn danger';
    del.innerHTML = `${renderIcon(Trash2)} <span class="ed-iconbtn-label">Delete</span>`;
    del.onclick = () => {
      const next = behaviors.slice();
      next.splice(idx, 1);
      this.commit({ ...json, behaviors: next }, 'structural');
      this.state.clearSelection();
    };
    actions.appendChild(del);
    this.root.appendChild(actions);
  }

  private replaceBehavior(json: MapJSON, idx: number, next: BehaviorDef): void {
    const behaviors = (json.behaviors ?? []).slice();
    behaviors[idx] = next;
    this.commit({ ...json, behaviors }, 'structural');
  }

  // ---------------------------------------------------------------------
  // commit + form helpers

  private commit(next: MapJSON, kind: CommitKind, syncedIds?: string[]): void {
    this.state.commit(next, { kind, syncedIds });
  }

  private h2(title: string, icon: IconNode): void {
    const h = document.createElement('div');
    h.className = 'ed-h2';
    h.innerHTML = `${renderIcon(icon)}<span>${escapeHtml(title)}</span>`;
    this.root.appendChild(h);
  }

  private section(id: string, title: string, icon: IconNode): HTMLElement {
    const s = this.sections.get(id) ?? { collapsed: false };
    this.sections.set(id, s);
    const wrap = document.createElement('div');
    wrap.className = 'ed-section' + (s.collapsed ? ' is-collapsed' : '');
    const head = document.createElement('div');
    head.className = 'ed-section-header';
    head.innerHTML = `${renderIcon(icon)}<span style="flex:1">${escapeHtml(title)}</span><span class="ed-icon chev">${renderIcon(ChevronDown, 14)}</span>`;
    head.onclick = () => {
      s.collapsed = !s.collapsed;
      wrap.classList.toggle('is-collapsed', s.collapsed);
    };
    const body = document.createElement('div');
    body.className = 'ed-section-body';
    wrap.appendChild(head);
    wrap.appendChild(body);
    this.root.appendChild(wrap);
    return body;
  }

  private textField(parent: HTMLElement, label: string, value: string, onChange: (v: string) => void): void {
    const row = makeRow(label);
    const input = document.createElement('input');
    input.type = 'text';
    input.value = value;
    input.className = 'ed-input';
    input.addEventListener('change', () => onChange(input.value));
    row.appendChild(input);
    parent.appendChild(row);
  }

  private numberField(parent: HTMLElement, label: string, value: number, onChange: (v: number) => void): void {
    const row = makeRow(label);
    row.appendChild(makeNumeric(value, onChange));
    parent.appendChild(row);
  }

  private sliderField(parent: HTMLElement, label: string, value: number, min: number, max: number, step: number, onChange: (v: number) => void): void {
    const row = document.createElement('div');
    row.className = 'ed-slider-row';
    row.innerHTML = `<label>${escapeHtml(label)}</label>`;
    const range = document.createElement('input');
    range.type = 'range';
    range.className = 'ed-range';
    range.min = String(min); range.max = String(max); range.step = String(step);
    range.value = String(value);
    const num = document.createElement('input');
    num.type = 'number';
    num.className = 'ed-numeric';
    num.step = String(step);
    num.value = String(value);
    const fire = () => {
      const v = parseFloat(num.value);
      if (!Number.isNaN(v)) onChange(v);
    };
    range.addEventListener('input', () => { num.value = range.value; });
    range.addEventListener('change', () => { num.value = range.value; fire(); });
    num.addEventListener('change', () => { range.value = num.value; fire(); });
    row.appendChild(range); row.appendChild(num);
    parent.appendChild(row);
  }

  private colorField(parent: HTMLElement, label: string, value: string, onChange: (v: string) => void): void {
    const row = makeRow(label);
    const input = document.createElement('input');
    input.type = 'color';
    input.className = 'ed-color';
    input.value = normalizeHex(value);
    input.addEventListener('change', () => onChange(input.value));
    row.appendChild(input);
    parent.appendChild(row);
  }

  private checkboxField(parent: HTMLElement, label: string, checked: boolean, onChange: (v: boolean) => void): void {
    const lbl = document.createElement('label');
    lbl.className = 'ed-checkbox';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = checked;
    input.addEventListener('change', () => onChange(input.checked));
    const txt = document.createElement('span');
    txt.textContent = label;
    lbl.appendChild(input); lbl.appendChild(txt);
    parent.appendChild(lbl);
  }

  private selectField(parent: HTMLElement, label: string, value: string, options: string[], onChange: (v: string) => void): void {
    const row = makeRow(label);
    const sel = document.createElement('select');
    sel.className = 'ed-select';
    if (!options.includes(value)) options = [value, ...options];
    for (const o of options) {
      const opt = document.createElement('option');
      opt.value = o; opt.textContent = o;
      if (o === value) opt.selected = true;
      sel.appendChild(opt);
    }
    sel.addEventListener('change', () => onChange(sel.value));
    row.appendChild(sel);
    parent.appendChild(row);
  }

  private vec3Field(parent: HTMLElement, label: string, value: Vec3, onChange: (v: Vec3) => void): void {
    const row = document.createElement('div');
    row.className = 'ed-row';
    row.innerHTML = `<label>${escapeHtml(label)}</label>`;
    const grid = document.createElement('div');
    grid.className = 'ed-row-3col';
    const v = [value[0], value[1], value[2]] as [number, number, number];
    for (let i = 0; i < 3; i++) {
      const inp = makeNumeric(v[i]!, (n) => {
        v[i] = n;
        onChange([v[0], v[1], v[2]] as Vec3);
      });
      grid.appendChild(inp);
    }
    row.appendChild(grid);
    parent.appendChild(row);
  }

  private jsonField(parent: HTMLElement, label: string, value: unknown, onChange: (v: Record<string, unknown>) => void): void {
    const wrap = document.createElement('div');
    const lbl = document.createElement('label');
    lbl.style.cssText = 'font-size:11px;color:var(--fg-mute);display:block;margin-bottom:4px';
    lbl.textContent = label;
    const ta = document.createElement('textarea');
    ta.className = 'ed-textarea';
    ta.rows = 6;
    ta.value = JSON.stringify(value, null, 2);
    ta.addEventListener('change', () => {
      try {
        const parsed = JSON.parse(ta.value || '{}');
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          onChange(parsed as Record<string, unknown>);
          ta.classList.remove('is-error');
        } else {
          ta.classList.add('is-error');
        }
      } catch {
        ta.classList.add('is-error');
      }
    });
    wrap.appendChild(lbl); wrap.appendChild(ta);
    parent.appendChild(wrap);
  }

  /** Texture-slot row: [thumbnail | name | upload | clear]. The thumbnail
   *  doubles as the file picker — clicking it opens the OS dialog. */
  private textureSlot(parent: HTMLElement, label: string, url: string | undefined, onSet: (url: string | undefined) => void): void {
    const row = document.createElement('div');
    row.className = 'ed-row';
    row.innerHTML = `<label>${escapeHtml(label)}</label>`;
    const right = document.createElement('div');
    right.style.cssText = 'display:flex;gap:6px;align-items:center;min-width:0';
    const thumb = document.createElement('div');
    thumb.style.cssText = 'width:28px;height:28px;border-radius:4px;border:1px solid var(--bd);background:var(--s2) center/cover no-repeat;cursor:pointer;flex:0 0 auto';
    thumb.title = 'Click to upload';
    if (url) {
      const display = isAssetUrl(url) ? (getAsset(parseAssetUrl(url)!)?.blobUrl ?? '') : url;
      if (display) thumb.style.backgroundImage = `url("${display}")`;
    }
    thumb.onclick = () => this.pickTextureFile(onSet);
    right.appendChild(thumb);
    const name = document.createElement('div');
    name.style.cssText = 'font-size:11px;color:var(--fg-mute);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0';
    if (url) {
      name.textContent = isAssetUrl(url) ? (getAsset(parseAssetUrl(url)!)?.name ?? url) : url.split('/').pop() ?? url;
    } else {
      name.textContent = '(none)';
    }
    right.appendChild(name);
    if (url) {
      const clear = document.createElement('button');
      clear.className = 'ed-iconbtn compact danger';
      clear.title = 'Clear texture';
      clear.innerHTML = renderIcon(Trash2, 12);
      clear.onclick = () => onSet(undefined);
      right.appendChild(clear);
    }
    row.appendChild(right);
    parent.appendChild(row);
  }

  private pickTextureFile(onSet: (url: string | undefined) => void): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      const asset = await registerAssetFromFile(f);
      onSet(makeAssetUrl(asset.id));
    };
    input.click();
  }

  private vec2Field(parent: HTMLElement, label: string, value: readonly [number, number], onChange: (v: readonly [number, number]) => void): void {
    const row = document.createElement('div');
    row.className = 'ed-row';
    row.innerHTML = `<label>${escapeHtml(label)}</label>`;
    const grid = document.createElement('div');
    grid.className = 'ed-row-2col';
    const v: [number, number] = [value[0], value[1]];
    grid.appendChild(makeNumeric(v[0], (n) => { v[0] = n; onChange([v[0], v[1]]); }));
    grid.appendChild(makeNumeric(v[1], (n) => { v[1] = n; onChange([v[0], v[1]]); }));
    row.appendChild(grid);
    parent.appendChild(row);
  }

  private uploadField(parent: HTMLElement, label: string, _value: string, accept: string, onFile: (f: File) => void | Promise<void>): void {
    const row = makeRow(label);
    const btn = document.createElement('button');
    btn.className = 'ed-iconbtn';
    btn.innerHTML = `${renderIcon(Upload)}<span class="ed-iconbtn-label">Upload</span>`;
    btn.onclick = () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.onchange = async () => {
        const f = input.files?.[0];
        if (f) await onFile(f);
      };
      input.click();
    };
    row.appendChild(btn);
    parent.appendChild(row);
  }
}

function makeRow(label: string): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'ed-row';
  row.innerHTML = `<label>${escapeHtml(label)}</label>`;
  return row;
}

function makeNumeric(value: number, onChange: (v: number) => void): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'number';
  input.step = 'any';
  input.value = String(value);
  input.className = 'ed-numeric';
  input.style.width = '100%';
  input.addEventListener('change', () => {
    const v = parseFloat(input.value);
    if (!Number.isNaN(v)) onChange(v);
  });
  return input;
}

function normalizeHex(c: string): string {
  if (!c) return '#000000';
  if (c[0] === '#' && (c.length === 7 || c.length === 4)) return c;
  return '#000000';
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    c === '&' ? '&amp;' :
    c === '<' ? '&lt;' :
    c === '>' ? '&gt;' :
    c === '"' ? '&quot;' : '&#39;'
  ));
}

interface ObjectPathStep { index: number; node: ObjectNode; }
type ObjectPath = ObjectPathStep[];

function findObjectPath(objects: ObjectNode[], id: string): ObjectPath | null {
  for (let i = 0; i < objects.length; i++) {
    const n = objects[i]!;
    const nId = n.id ?? `(${n.type})`;
    if (nId === id) return [{ index: i, node: n }];
    if (n.type === 'group' && n.children?.length) {
      const sub = findObjectPath(n.children, id);
      if (sub) return [{ index: i, node: n }, ...sub];
    }
  }
  return null;
}

function iconForType(t: string): IconNode {
  switch (t) {
    case 'box': return Box;
    case 'sphere': return Cuboid;
    case 'cylinder': return Pyramid;
    case 'capsule': return Cuboid;
    case 'plane': return Maximize2;
    case 'circle': return Cuboid;
    case 'group': return Move;
    case 'modelRef': return Box;
    default: return Box;
  }
}

void Rotate3d;
